import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import { clientQuery } from "#modules/clients/infra/client.query";
import { clientsQuery } from "#modules/clients/infra/clients.query";
import { ContactDto } from "#modules/contacts/infra/Contact.dto";
import { createContact, CreateContactDto } from "#modules/contacts/infra/createContact.api";

export const useCreateContact = ({
  clientId,
  options,
}: {
  clientId: string;
  options?: {
    onSuccess?: (data: ContactDto) => void;
  };
}) => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (values: Exclude<CreateContactDto, "clientId">) => createContact({ ...values }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries(clientQuery(clientId || ""));
      void queryClient.invalidateQueries(clientsQuery());

      if (data) {
        options?.onSuccess?.(data);
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.data?.message) {
        const { message } = error.response.data;
        logger.error(error.message);
        showErrorToast({ title: message });
      } else {
        showErrorToast({
          title: "Une erreur est survenue",
          message: "impossible de créer le contact",
        });
      }
    },
  });

  return { createContact: mutate, createContactAsync: mutateAsync, ...rest };
};
